import type { Entity } from '@backstage/catalog-model';
import type { BadgeColor } from '../../components/ui';

const SOURCE_LOCATION_ANNOTATION = 'backstage.io/source-location';

/** Owner of the entity: the first `ownedBy` relation, if any. */
export function getOwnerRef(entity: Entity): string | undefined {
  return entity.relations?.find(relation => relation.type === 'ownedBy')
    ?.targetRef;
}

const ENTITY_REF_PATTERN = /^([^:/\s]+):([^:/\s]+)\/([^:/\s]+)$/;

/** In-app catalog route for a full entity ref like `group:default/web`. */
export function entityRefRoute(ref: string): string | undefined {
  const match = ENTITY_REF_PATTERN.exec(ref);
  if (!match) return undefined;
  const [, kind, namespace, name] = match;
  return `/catalog/${namespace}/${kind.toLocaleLowerCase('en-US')}/${name}`;
}

/** The browsable URL behind the source-location annotation (`url:...`). */
export function getSourceUrl(entity: Entity): string | undefined {
  const location = entity.metadata.annotations?.[SOURCE_LOCATION_ANNOTATION];
  if (!location?.startsWith('url:')) return undefined;
  return location.slice('url:'.length);
}

/** Compact display label for a source URL: host + path, no scheme/slash. */
export function sourceLabel(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

const LIFECYCLE_COLORS: Record<string, BadgeColor> = {
  production: 'success',
  experimental: 'warning',
  deprecated: 'error',
};

export const lifecycleColor = (lc: string): BadgeColor =>
  LIFECYCLE_COLORS[lc] ?? 'gray';
